// ANGULAR
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { ConfiguracionesService } from '@api-requests-services/configuraciones.service';
// MODELS
import { Configuration } from '@models/configuraciones.model';

export enum Views {
  Home = 'home',
  Configurations = 'configurations',
  ConfigurationDetails = 'configurationDetails',
  EditingConfiguration = 'editingConfiguration',
  CreatingConfiguration = 'creatingConfiguration',
}

@Component({
  selector: 'app-configuraciones',
  templateUrl: './configuraciones.component.html',
  styleUrl: './configuraciones.component.css',
  imports: [
    FormsModule,
    CommonModule
  ]
})
export class ConfiguracionesComponent implements OnInit {
  allConfigurations: Configuration[];
  configurations: Configuration[];
  newConfiguration: Configuration;
  configurationSearched: Configuration | null;
  configurationSelected: Configuration;
  configurationNameToSearch: string;
  configurationToSearch: string;
  // Control de contenido html
  views = Views;
  currentView: string = Views.Home;
  configurationNotFound: boolean = false;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private configuracionesService: ConfiguracionesService,
    private title: Title
  ) {
    this.title.setTitle('Oráculo - Configuraciones');
  }

  ngOnInit(): void { }

  selectView(view: string, configurationSelected?: Configuration): void {
    if (configurationSelected) {
      this.configurationSelected = configurationSelected;
      this.configurationSelected.valor = this.formatObjectValueIfJson(this.configurationSelected);
    }
    this.currentView = view;

    switch (this.currentView) {
      case Views.Home:
        this.configurationSearched = null;
        this.configurationNameToSearch = '';
        this.configurationToSearch = '';
        break;
      case Views.Configurations:
        this.getConfigurations();
        break;
      case Views.ConfigurationDetails:
        this.configurationToSearch = '';
        this.newConfiguration = { ...this.configurationSelected };
        break;
      case Views.EditingConfiguration:
        break;
      case Views.CreatingConfiguration:
        this.newConfiguration = { nombre: '', valor: '' };
        break;
    }
  }

  getConfigurations(): void {
    this.configuracionesService.getConfiguraciones().subscribe(
      (configurations) => {
        if (configurations) {
          // console.log('Configuraciones: ', configurations);
          this.allConfigurations = configurations.map(configuration => ({ nombre: configuration.nombre, valor: configuration.valor }));
          this.configurations = this.allConfigurations;
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Configurations:', error);
        }
      }
    );
  }

  createConfiguration(): void {
    if (this.newConfiguration) {
      if (this.validateAndParseInputJson(this.newConfiguration)) {
        this.configuracionesService.postCreateConfiguracion(this.newConfiguration).subscribe(
          () => {
            this.selectView(Views.ConfigurationDetails, this.newConfiguration);
            this.alertService.showAutoCloseMessage(`¡Configuración creada!`, ``, "success", 1200);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error creating Configuration:', error);
            }
          }
        );
      }
    }
  }

  getConfiguration(): void {
    if (this.configurationNameToSearch) {
      this.configuracionesService.getConfiguracion(this.configurationNameToSearch).subscribe(
        (configuration) => {
          if (configuration) {
            // console.log('Configuración: ', configuration);
            this.configurationNotFound = false;
            this.configurationSearched = configuration;
            this.configurationSearched.valor = this.formatObjectValueIfJson(this.configurationSearched);
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            if (error.status === 404) {
              this.configurationNotFound = true;
            } else {
              this.configurationNotFound = false;
              this.alertService.swalError(error.error);
            }
            console.error('Error getting Configuration:', error);
          }
        }
      );
    }
  }

  editConfiguration(): void {
    if (this.newConfiguration) {
      if (this.validateAndParseInputJson(this.newConfiguration)) {
        this.configuracionesService.putEditConfiguracion(this.configurationSelected.nombre, this.newConfiguration).subscribe(
          () => {
            this.selectView(Views.Configurations);
            this.alertService.showAutoCloseMessage(`¡Configuración "${this.configurationSelected.nombre}" editada!`, ``, "success", 1200);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error editing Configuration:', error);
            }
          }
        );
      }
    }
  }

  deleteConfiguration(): void {
    this.alertService.showConfirmAlert(
      `Eliminar Configuración`,
      `¿Seguro que quieres eliminar la Configuración con nombre "${this.configurationSelected.nombre}"?`
    ).then((result) => {
      if (result.isConfirmed && this.configurationSelected) {
        this.configuracionesService.deleteConfiguracion(this.configurationSelected.nombre).subscribe(
          () => {
            this.selectView(Views.Configurations);
            this.alertService.showAutoCloseMessage(`¡Configuración eliminada!`, ``, "success", 1200);
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting Configuration:', error);
            }
          }
        );
      }
    });
  }

  formatObjectValueIfJson(configuration: Configuration): any {
    if (typeof configuration.valor === 'object' && configuration.valor !== null) {
      try {
        const parsedJson: string = JSON.stringify(configuration.valor, null, 2)
        return parsedJson;
      } catch {
        this.alertService.showAutoCloseMessage(`Formato JSON inválido de la configuración "${configuration.nombre}"`, ``, "error", 1200);
        return configuration.valor;
      }
    } else {
      return configuration.valor;
    }
  }

  validateAndParseInputJson(configuration: Configuration): boolean {
    if (configuration.valor.trim().startsWith("{")) {
      try {
        this.newConfiguration.valor = JSON.parse(configuration.valor);
        return true;
      } catch {
        this.alertService.showAutoCloseMessage(`Formato JSON inválido del valor escrito`, ``, "error", 1200);
        return false;
      }
    } else {
      return true;
    }
  }

  onSearchChange(): void {
    this.configurations = this.allConfigurations.filter(configuration => configuration.nombre.toLowerCase().includes(this.configurationToSearch.toLowerCase()));

    if (this.configurationToSearch === undefined || this.configurationToSearch === '' || this.configurationToSearch === ' ') {
      this.configurations = this.allConfigurations;
    }
  }
}